/**
 * Settings chosen before a game starts, from the start menu or the join screen of a split-screen
 * race: the ghosts (the other player's, the best-time run's), the tube over the game, the music,
 * when a race ends, replay cams, each seat's pad scheme, and each seat's headband. Kept in
 * localStorage; storage that is missing or full leaves them working for the session.
 *
 * Adding a setting is one field here with its default, and one row in the settings menu
 * (`settingsItems` in main.ts) if it is chosen from the menu.
 */

import { DEFAULT_PAD_SCHEME } from "@lache/input";
import { HEADBANDS } from "@lache/render";
import { MUSIC_LEVELS } from "./music";

/**
 * Seats a split-screen race has. Two stack as rows; three and four take a grid (`viewportCells` in
 * main.ts), which is as far as a screen goes before a ninja is too small to read.
 */
export const MAX_PLAYERS = 4;

export interface GameSettings {
  /** Draw the other players as translucent ghosts in each player's half of a split-screen race. */
  ghost: boolean;
  /** In a time trial, run the player's fastest saved run beside them as a ghost (`docs/replays.md`). */
  bestGhost: boolean;
  /** A replay of a race shows every player's cam (true) or only the picked player's. */
  replayAllCams: boolean;
  /** The tube over the game: scanlines and dark corners on the canvas (the menus wear theirs regardless). */
  crt: boolean;
  /** The synthwave loop's volume step: 0 off, then quiet, normal, loud (`MUSIC_LEVELS` in music.ts). */
  musicLevel: number;
  /** Touch-only help for flight posture and continuous bar-to-bar release gestures. */
  touchLacheHelp: boolean;
  /** Contextual lessons in solo Gym; off leaves free practice unchanged. */
  gymTutorial: boolean;
  /**
   * A race ends at the first finish (the rest did not finish) when true; otherwise it runs until
   * every player has finished or someone forfeits from the pause menu (the owner's default).
   */
  raceEndsFirst: boolean;
  /**
   * Pad scheme per seat. Seat 0 follows `tuning.padScheme` (the controls menu, F6, the F1 panel),
   * so its entry here is unused; the others are only set here.
   */
  schemes: number[];
  /** Headband color per seat, an index into HEADBANDS. */
  bands: number[];
}

export const DEFAULT_SETTINGS: GameSettings = {
  ghost: true,
  bestGhost: false,
  replayAllCams: true,
  crt: true,
  musicLevel: 2,
  touchLacheHelp: true,
  gymTutorial: true,
  raceEndsFirst: false,
  schemes: [DEFAULT_PAD_SCHEME, DEFAULT_PAD_SCHEME, DEFAULT_PAD_SCHEME, DEFAULT_PAD_SCHEME],
  bands: [0, 1, 2, 3],
};

const SETTINGS_KEY = "lache.settings.v1";

function safeStorage(): Storage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

function intList(v: unknown, defaults: number[], max: number): number[] {
  const out = defaults.slice();
  if (!Array.isArray(v)) return out;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    const n = v[i];
    if (typeof n === "number" && Number.isInteger(n) && n >= 0 && n < max) out[i] = n;
  }
  return out;
}

/** The saved settings over the defaults; anything unreadable falls back to its default. */
export function loadSettings(storage: Storage | null = safeStorage(), schemeCount = 64): GameSettings {
  const out: GameSettings = {
    ghost: DEFAULT_SETTINGS.ghost,
    bestGhost: DEFAULT_SETTINGS.bestGhost,
    replayAllCams: DEFAULT_SETTINGS.replayAllCams,
    crt: DEFAULT_SETTINGS.crt,
    musicLevel: DEFAULT_SETTINGS.musicLevel,
    touchLacheHelp: DEFAULT_SETTINGS.touchLacheHelp,
    gymTutorial: DEFAULT_SETTINGS.gymTutorial,
    raceEndsFirst: DEFAULT_SETTINGS.raceEndsFirst,
    schemes: DEFAULT_SETTINGS.schemes.slice(),
    bands: DEFAULT_SETTINGS.bands.slice(),
  };
  try {
    const raw = storage?.getItem(SETTINGS_KEY);
    if (!raw) return out;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (typeof parsed !== "object" || parsed === null) return out;
    if (typeof parsed.ghost === "boolean") out.ghost = parsed.ghost;
    if (typeof parsed.bestGhost === "boolean") out.bestGhost = parsed.bestGhost;
    if (typeof parsed.replayAllCams === "boolean") out.replayAllCams = parsed.replayAllCams;
    if (typeof parsed.crt === "boolean") out.crt = parsed.crt;
    if (typeof parsed.touchLacheHelp === "boolean") out.touchLacheHelp = parsed.touchLacheHelp;
    if (typeof parsed.gymTutorial === "boolean") out.gymTutorial = parsed.gymTutorial;
    if (typeof parsed.musicLevel === "number" && Number.isInteger(parsed.musicLevel)) {
      out.musicLevel = Math.min(MUSIC_LEVELS.length - 1, Math.max(0, parsed.musicLevel));
    }
    if (typeof parsed.raceEndsFirst === "boolean") out.raceEndsFirst = parsed.raceEndsFirst;
    out.schemes = intList(parsed.schemes, out.schemes, schemeCount);
    out.bands = intList(parsed.bands, out.bands, HEADBANDS.length);
  } catch {
    // Unreadable: the defaults.
  }
  return out;
}

export function saveSettings(settings: GameSettings, storage: Storage | null = safeStorage()): void {
  try {
    storage?.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // Storage unavailable or full: the settings last until reload.
  }
}
